/**
 * BulkReportingLineDialog — set the reporting line of many positions at once.
 *
 * Driven from the org Positions tab: the operator has multi-selected a set of
 * live (non-RETIRED) seats and wants them all to report to one parent position.
 * We POST the whole batch, then POLL the server job until it's done, streaming a
 * per-position progress readout.
 *
 * The write is position-anchored, so VACANT seats resolve fast (a
 * PositionHierarchies 'Line' edge). FILLED seats run the worker-anchored
 * 'employment' saga and take noticeably longer — that's expected and surfaced.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, GitBranch, Loader2, XCircle, Clock, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import organizationService, {
  type Position, type BulkReportsToJob, type BulkReportsToResultItem,
} from '@/services/api/organizationService';
import { AsyncSearchCombobox } from './AsyncSearchCombobox';

export interface BulkReportingLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** F&O position codes of the seats to re-parent (already filtered to eligible). */
  selectedCodes: string[];
  /** Fired once when the job completes — the parent clears the selection + reloads. */
  onCompleted: () => void;
}

type Phase = 'idle' | 'submitting' | 'running' | 'done' | 'error';

const POLL_INTERVAL_MS = 1500;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

function extractError(err: unknown): string | undefined {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
    (err as { message?: string })?.message
  );
}

/** Compact per-position outcome row in the progress list. */
const ResultRow: React.FC<{ item: BulkReportsToResultItem }> = ({ item }) => {
  const statusNode = (() => {
    switch (item.status) {
      case 'ok':
        return <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" />Done</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="h-3.5 w-3.5" />Failed</span>;
      case 'partial':
        return <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3.5 w-3.5" />Partial</span>;
      default:
        return <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Pending</span>;
    }
  })();

  return (
    <div className="flex items-start justify-between gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-muted/40">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-foreground">{item.position_code}</span>
          <Badge
            variant="outline"
            className={item.mode === 'vacant'
              ? 'border-amber-300 text-amber-700 dark:border-amber-500/40 dark:text-amber-300'
              : 'border-heritage-blue/40 text-heritage-blue dark:text-heritage-blue-100'}
          >
            {item.mode === 'vacant' ? 'Vacant' : 'Filled'}
          </Badge>
        </div>
        {item.error && (
          <p className="mt-0.5 break-words text-xs text-destructive">{item.error}</p>
        )}
      </div>
      <div className="shrink-0 text-xs">{statusNode}</div>
    </div>
  );
};

export const BulkReportingLineDialog: React.FC<BulkReportingLineDialogProps> = ({
  open,
  onOpenChange,
  selectedCodes,
  onCompleted,
}) => {
  const [parent, setParent] = useState<Position | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<BulkReportsToJob | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  // Keep the completion callback in a ref so the poll effect never restarts when
  // the parent passes a new function identity.
  const onCompletedRef = useRef(onCompleted);
  useEffect(() => { onCompletedRef.current = onCompleted; }, [onCompleted]);
  const completedFiredRef = useRef(false);

  // Fresh state every time the dialog is opened.
  useEffect(() => {
    if (open) {
      setParent(null);
      setPhase('idle');
      setJobId(null);
      setJob(null);
      setPostError(null);
      setPollError(null);
      completedFiredRef.current = false;
    }
  }, [open]);

  // A seat can't report to one of the seats being changed — exclude the current
  // selection from the parent picker.
  const selectedSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);
  const searchParents = useCallback(
    async (q: string) => {
      const positions = await organizationService.listPositions({
        query: q,
        limit: 20,
        // Live seats only — never point a batch at a RETIRED/FROZEN/PENDING seat.
        status: ['ACTIVE', 'VACANT'],
      });
      return positions.filter((p) => !selectedSet.has(p.position_code));
    },
    [selectedSet],
  );

  const confirm = async () => {
    if (!parent || selectedCodes.length === 0) return;
    setPhase('submitting');
    setPostError(null);
    try {
      const { job_id } = await organizationService.bulkSetReportsTo(
        selectedCodes,
        parent.position_code,
      );
      setJobId(job_id);
      setPhase('running');
    } catch (err) {
      setPostError(extractError(err) || 'Could not start the bulk reporting-line job.');
      setPhase('idle');
    }
  };

  // Poll the job while it's running. Tolerates a couple of transient poll
  // failures before surfacing an error.
  useEffect(() => {
    if (phase !== 'running' || !jobId) return;
    let cancelled = false;
    let consecutiveErrors = 0;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const res = await organizationService.getBulkReportsToJob(jobId);
        if (cancelled) return;
        consecutiveErrors = 0;
        setJob(res);
        if (res.status === 'completed') {
          setPhase('done');
          return;
        }
      } catch (err) {
        if (cancelled) return;
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
          setPollError(
            extractError(err) ||
              'Lost contact with the bulk job. It may still be running on the server — reload to check.',
          );
          setPhase('error');
          return;
        }
      }
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [phase, jobId]);

  // On completion: toast + let the parent clear selection & reload. Fire once.
  useEffect(() => {
    if (phase !== 'done' || completedFiredRef.current) return;
    completedFiredRef.current = true;
    const ok = job?.results.filter((r) => r.status === 'ok').length ?? 0;
    const bad = job?.results.filter((r) => r.status === 'failed' || r.status === 'partial').length ?? 0;
    if (bad === 0) {
      toast.success(`Reporting line set for ${ok} position${ok === 1 ? '' : 's'}.`);
    } else {
      toast.error(`${ok} updated, ${bad} did not apply.`);
    }
    onCompletedRef.current?.();
  }, [phase, job]);

  const parentIsVacant = !!parent && (parent.is_vacant || !parent.incumbent_name);
  const total = job?.total ?? selectedCodes.length;
  const done = job?.done ?? 0;
  const okCount = job?.results.filter((r) => r.status === 'ok').length ?? 0;
  const badCount = job?.results.filter((r) => r.status === 'failed' || r.status === 'partial').length ?? 0;
  const showProgress = phase === 'running' || phase === 'done' || phase === 'error';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-heritage-blue" />
            Set reporting line
          </DialogTitle>
          <DialogDescription>
            {showProgress
              ? 'Applying the reporting-line change across the selected positions.'
              : `Point ${selectedCodes.length} selected position${selectedCodes.length === 1 ? '' : 's'} at one parent. Takes effect in F&O — no effective date.`}
          </DialogDescription>
        </DialogHeader>

        {/* ── Setup ─────────────────────────────────────────────────────────── */}
        {!showProgress && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Parent position</Label>
              <AsyncSearchCombobox<Position>
                search={searchParents}
                getKey={(p) => p.position_code}
                getPrimary={(p) => p.incumbent_name || 'Vacant'}
                getSecondary={(p) => `${p.title} · ${p.position_code}`}
                onSelect={setParent}
                placeholder="Search positions by title or code…"
                disabled={phase === 'submitting'}
                emptyText="No positions match."
              />
              {parent && (
                <p className="text-xs text-muted-foreground">
                  Selected parent:{' '}
                  <span className="font-medium text-foreground">
                    {parent.incumbent_name || 'Vacant'}
                  </span>{' '}
                  ({parent.title} · {parent.position_code})
                </p>
              )}
            </div>

            {parentIsVacant && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  This parent position is vacant. Filled positions will report to it in F&amp;O,
                  and their Report To in Sage becomes the person acting for it: each
                  position&apos;s recorded absentee approver, otherwise the first filled position
                  above the parent.
                </p>
              </div>
            )}

            {postError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive dark:bg-destructive/10">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="min-w-0 break-words">{postError}</p>
              </div>
            )}
          </div>
        )}

        {/* ── Progress ──────────────────────────────────────────────────────── */}
        {showProgress && (
          <div className="space-y-3">
            {phase === 'done' && (
              <div
                className={badCount === 0
                  ? 'flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300'}
              >
                {badCount === 0
                  ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
                <p className="font-medium">
                  {okCount} updated{badCount > 0 ? ` · ${badCount} failed or partial` : ''}.
                </p>
              </div>
            )}

            {pollError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive dark:bg-destructive/10">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="min-w-0 break-words">{pollError}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  {phase === 'running' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {phase === 'running' ? 'Applying…' : 'Complete'}
                </span>
                <span className="font-medium text-foreground">{done} / {total}</span>
              </div>
              <Progress value={total > 0 ? (done / total) * 100 : 0} />
              {phase === 'running' && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Vacant seats resolve quickly; filled seats run a saga and take longer.
                </p>
              )}
            </div>

            {job && job.results.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-md border border-border p-1">
                {job.results.map((item) => (
                  <ResultRow key={item.position_code} item={item} />
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!showProgress ? (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={confirm} disabled={!parent || phase === 'submitting'}>
                {phase === 'submitting' ? (
                  <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Starting…</>
                ) : (
                  <><GitBranch className="mr-1.5 h-4 w-4" />Set reporting line ({selectedCodes.length})</>
                )}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant={phase === 'running' ? 'outline' : 'default'}
              onClick={() => onOpenChange(false)}
            >
              {phase === 'running' ? 'Close (keeps running)' : 'Done'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkReportingLineDialog;
